import React from "react";
import PropTypes from "prop-types";
import { Card, CardHeader, CardBody, CardFooter, Button, ButtonGroup } from "shards-react";
import Chart from "../../utils/chart";

function getThousandK(num) {
  return Math.abs(num) > 999 ? Math.sign(num) * ((Math.abs(num) / 1000).toFixed(1)) + 'k' : Math.sign(num) * Math.abs(num)
}

function selectConfirmed(data) {
  for (const key of ['totalConfirmed', 'stateConfirmed', 'countyConfirmed']) {
    if (data[key] != undefined) {
      return data[key]
    }
  }
}

function selectDeaths(data) {
  for (const key of ['totalDeaths', 'stateDeaths', 'countyDeaths']) {
    if (data[key] != undefined) {
      return data[key]
    }
  }
}

function getTsToShow(ts, selectedState, selectedCounty) {
  // Just return national data
  if (!selectedState && !selectedCounty) return ts

  const tsToShow = []

  ts.forEach(data => {
    const date = data.date
    if (data.stateToCountyToData && data.stateToCountyToData[selectedState]) {
      const stateData = data.stateToCountyToData[selectedState]
      // Only state selected
      if (selectedState && !selectedCounty) {
        tsToShow.push({
          date,
          ...stateData
        })
      // County selected
      } else if (selectedState && selectedCounty && stateData.countyToData && stateData.countyToData[selectedCounty]) {
        const countyData = stateData.countyToData[selectedCounty]
        tsToShow.push({
          date,
          ...countyData
        })
      }
    }
  })

  return tsToShow
}

function calcDayDiffs(ts) {
  for (let i = 0; i < ts.length; i++) {
    const currDay = ts[i]
    if (i == 0) {
      // Confirmed
      currDay.newConfirmed = 0

      // Deaths
      currDay.newDeaths = 0
      continue
    }
    const prevDay = ts[i - 1]
    // Confirmed
    currDay.newConfirmed = selectConfirmed(currDay) - selectConfirmed(prevDay)

    // Deaths
    currDay.newDeaths = selectDeaths(currDay) - selectDeaths(prevDay)
  }
}

class UsersOverview extends React.PureComponent {
  constructor(props) {
    super(props);

    this.canvasRef = React.createRef();
    this.chart = null;
    this.state = {
      useLogScale: false,
      period: null,
      error: null,
      casesHidden: false,
      newCasesHidden: false,
      deathsHidden: true,
      newDeathsHidden: true,
      selectedState: null,
      selectedCounty: null
    }
  }


  createBigChartData(ts) {
    if (!ts) return


    const selectedState = this.props.selectedState
    const selectedCounty = this.props.selectedCounty

    let tsToShow = getTsToShow(ts, selectedState, selectedCounty)
    if (!tsToShow || tsToShow.length == 0) {
      return null
    }

    calcDayDiffs(tsToShow)

    if (this.state.period) {
      tsToShow = tsToShow.slice(-this.state.period)
    }

    const datasets = []

    if (!this.state.casesHidden) {
      datasets.push({
        // hidden: this.state.casesHidden,
        label: "Cases",
        fill: "start",
        data: tsToShow.map(entry => selectConfirmed(entry)),
        backgroundColor: "rgba(255, 165, 0, 0.1)",
        borderColor: "rgb(255, 165, 0)",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3
      })
    }

    if (!this.state.newCasesHidden) {
      datasets.push({
        // hidden: this.state.newCasesHidden,
        label: 'New Cases',
        data: tsToShow.map(entry => entry.newConfirmed),
        fill: "start",
        backgroundColor: "rgba(255, 165, 0, 0.1)",
        borderColor: "rgb(255, 165, 0)",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        borderDash: [10, 5]
      })
    }


    if (!this.state.deathsHidden) {
      datasets.push({
        // hidden: this.state.deathsHidden,
        label: "Deaths",
        fill: "start",
        data: tsToShow.map(entry => selectDeaths(entry)),
        backgroundColor: "rgba(255,0,0,0.1)",
        borderColor: "rgb(255,0,0)",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
      })
    }

    if (!this.state.newDeathsHidden) {
      datasets.push({
        // hidden: this.state.newDeathsHidden,
        label: "New Deaths",
        fill: "start",
        data: tsToShow.map(entry => entry.newDeaths),
        backgroundColor: "rgba(255,0,0,0.1)",
        borderColor: "rgb(255,0,0)",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        borderDash: [10, 5]
      })
    }

    return {
      // TODO: use date labels based on data available
      labels: tsToShow.map(entry => entry.date),
      datasets: datasets
    }
  }

  setUseLogScale = (useLogScale) => {
    if (this.state.useLogScale != useLogScale) {
      this.setState({ useLogScale: useLogScale })
    }
  }

  updateChart() {
    if (this.chart) this.chart.destroy();

    const chartData = this.createBigChartData(this.props.ts)
    if (!chartData || chartData.labels.length <= 1) {
      // TODO: display error message
      this.setState({error: "Sorry, we don't have historical data for this location yet. Please check back tomorrow."})
      return
    } else {
      this.setState({error: null})
    }

    const chartOptions = {
      ...{
        maintainAspectRatio: false,
        responsive: true,
        // layout: {
        //   padding: {
        //     left: 0,
        //     right: 0,
        //     top: 0,
        //     bottom: 0
        //   }
        // },
        title: {
          display: true,
          padding: 10,
          lineHeight: 1.5,
          fontSize: 14,
          text: ['Touch the chart to view datapoints.', 'Tap on legend to hide specific dataset.']
        },
        legend: {
          // onClick: (e, legendItem) => {
          //   var index = legendItem.datasetIndex;
          //   const ci = this.chart
          //   console.log(legendItem)
          //   ci.data.datasets.forEach((e, i) => {
          //     var meta = ci.getDatasetMeta(i);
          //     if (i === index) {
          //       const newHidden = !meta.hidden
          //       meta.hidden = newHidden;
          //       if (i == 0) {
          //         this.setState({ casesHidden: newHidden })
          //       }

          //     }
          //   });

          //   ci.update();
          // },
          position: "top",
          labels: {
            //   boxWidth: 40,
            padding: 15,
            fontSize: 14
          },
          onHover: function (e) {
            e.target.style.cursor = 'pointer'
          },
          onLeave: function (e) {
            e.target.style.cursor = 'default'
          }
        },
        elements: {
          line: {
            // A higher value makes the line look skewed at this ratio.
            tension: 0.1
          },
          point: {
            radius: 0
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: false,
              ticks: {
                callback(tick, index) {
                  return tick
                  // return index % 7 !== 0 ? "" : tick;
                }
              }
            }
          ],
          yAxes: [
            {
              type: this.state.useLogScale ? 'logarithmic' : 'linear',
              ticks: {
                // suggestedMax: 45,
                // autoSkip: true,
                min: 0,

                callback: (tick, index, values) => {
                  // if (tick < 0) return
                  if (this.state.useLogScale) {
                    if (index == 0) {
                      return getThousandK(tick)
                    }
                    if (tick == 100 || tick == 1000 || tick == 10000 || tick == 100000 || tick == 1000000) {
                      return getThousandK(tick)
                    }
                  } else {
                    return tick > 999 ? getThousandK(tick) : tick;
                  }
                }
              }
            }
          ]
        },
        hover: {
          mode: "index",
          intersect: false
        },
        tooltips: {
          custom: false,
          mode: "index",
          intersect: false
        }
      },
      ...this.props.chartOptions
    };

    this.chart = new Chart(this.canvasRef.current, {
      type: "LineWithLine",
      data: chartData,
      options: chartOptions
    });

    // They can still be triggered on hover.
    const buoMeta = this.chart.getDatasetMeta(0);
    buoMeta.data[0]._model.radius = 0;
    buoMeta.data[
      chartData.datasets[0].data.length - 1
    ]._model.radius = 0;

    // Render the chart.
    // this.chart.render();

  }

  componentDidMount() {
    this.updateChart()
  }

  componentDidUpdate(prevProps) {
    this.updateChart()


  }

  setPeriod(period) {
    if (this.state.period != period) {
      this.setState({period: period})
    }
  }

  getPeriodControl() {
    return (
      <ButtonGroup size="sm">
        <Button theme="white" active={this.state.period == 14} onClick={() => this.setPeriod(14)}>
          14 days
        </Button>
        <Button theme="white" active={!this.state.period} onClick={() => this.setPeriod(null)}>
          All days
        </Button>
      </ButtonGroup>
    )
  }

  setDatasetHidden(casesHidden, newCasesHidden, deathsHidden, newDeathsHidden) {
    this.setState({
      casesHidden: casesHidden,
      newCasesHidden: newCasesHidden,
      deathsHidden: deathsHidden,
      newDeathsHidden: newDeathsHidden
    })
  }

  isCasesSelected() {
    return !this.state.casesHidden && !this.state.newCasesHidden && this.state.deathsHidden && this.state.newDeathsHidden
  }

  isDeathsSelected() {
    return this.state.casesHidden && this.state.newCasesHidden && !this.state.deathsHidden && !this.state.newDeathsHidden
  }

  isAllSelected() {
    return !this.state.casesHidden && !this.state.newCasesHidden && !this.state.deathsHidden && !this.state.newDeathsHidden
  }

  getHeaderControl() {
    return (
      <div className="mt-2">
        <ButtonGroup size="sm" className="big-chart-header-btn-group">
          <Button theme="white" onClick={() => this.setDatasetHidden(false, false, true, true)} active={this.isCasesSelected()}>
            Cases
          </Button>
          <Button theme="white" onClick={() => this.setDatasetHidden(true, true, false, false)} active={this.isDeathsSelected()}>
            Deaths
          </Button>
          <Button theme="white" onClick={() => this.setDatasetHidden(false, false, false, false)} active={this.isAllSelected()}>
            All
          </Button>
        </ButtonGroup>
        &nbsp;&nbsp;&nbsp;
        <ButtonGroup size="sm" className="big-chart-header-btn-group">
          <Button theme="white" onClick={() => this.setUseLogScale(false)} active={!this.state.useLogScale}>
            linear
          </Button>
          <Button theme="white" onClick={() => this.setUseLogScale(true)} active={this.state.useLogScale}>
            log
          </Button>
        </ButtonGroup>
      </div>
    )
  }

  selectNationwide() {
    this.props.setSelectedState(null)
    this.props.setSelectedCounty(null)
  }

  render() {
    let location;
    const selectedState = this.props.selectedState
    const selectedCounty = this.props.selectedCounty
    if (!selectedState && !selectedCounty) {
      location = 'Nationwide'
    } else if (selectedState && !selectedCounty) {
      location = `${selectedState} State`
    } else {
      location = `${selectedCounty}, ${selectedState}`
    }
    return (
      <Card small className="h-100">
        <CardHeader className="border-bottom" style={{paddingBottom: '11px'}}>
          <h6 className="m-0"><u>{location}</u> Cases & Deaths</h6>
          {
            selectedState || selectedCounty ?
              <a href="javascript:void(0)" onClick={() => this.selectNationwide()}>(see nationwide)</a>
              : ''
          }
          {this.state.error ? <div className="bg-danger rounded p-3" style={{ color: 'white' }}>{this.state.error}</div> : ''}
          {this.getHeaderControl()}
        </CardHeader>
        <CardBody className="pt-0">
          {/* <Row className="border-bottom py-2 bg-light">
            <Col sm="6" className="d-flex mb-2 mb-sm-0">
              <RangeDatePicker />
            </Col>
            <Col>
              <Button
                size="sm"
                className="d-flex btn-white ml-auto mr-auto ml-sm-auto mr-sm-0 mt-3 mt-sm-0"
              >
                View Full Report &rarr;
              </Button>
            </Col>
          </Row> */}
          <canvas
            height="300"
            ref={this.canvasRef}
            style={{ maxWidth: "100% !important" }}
          />
        </CardBody>
        <CardFooter className="text-center pt-0">
          {this.getPeriodControl()}
        </CardFooter>
      </Card>
    );
  }
}

UsersOverview.propTypes = {
  /**
   * The component's title.
   */
  title: PropTypes.string,
  /**
   * The chart dataset.
   */
  chartData: PropTypes.object,
  /**
   * The Chart.js options.
   */
  chartOptions: PropTypes.object
};


UsersOverview.defaultProps = {
};

export default UsersOverview;
